/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { IRoadDetails } from '../models';

export type DataCompletenessStatus = 'Full' | 'Partial' | 'Complete Miss';

export interface PendoDataCompletenessPayload {
  Data_Completeness: DataCompletenessStatus;
  Error?: string;
  Road_Name?: string | null;
  City_Name?: string | null;
  Latitude?: number | null;
  Longitude?: number | null;
  Segment_ID?: string | null;
}

/**
 * Computes the data completeness classification for a road segment.
 * - 'Full': both roadName and cityName are present
 * - 'Partial': exactly one of roadName or cityName is missing
 * - 'Complete Miss': both roadName and cityName are missing
 *
 * @param roadDetails - The road details object from the segment info response
 */
export function computeDataCompleteness(roadDetails: IRoadDetails): DataCompletenessStatus {
  const roadMissing: boolean = roadDetails.roadName == null;
  const cityMissing: boolean = roadDetails.cityName == null;

  if (roadMissing && cityMissing) return 'Complete Miss';
  if (roadMissing || cityMissing) return 'Partial';
  return 'Full';
}

/**
 * Builds the full Pendo event payload based on the data completeness scenario.
 * - Full: only Data_Completeness property
 * - Partial: includes error description, the present name field, coordinates, and segment ID
 * - Complete Miss: only Data_Completeness property
 *
 * @param roadDetails - The road details object from the segment info response
 * @param mapCenter - The map center coordinates (lat/lng), may be null
 * @param selectedSegmentId - The selected segment ID from the store, may be null
 */
export function buildPendoDataCompletenessPayload(
  roadDetails: IRoadDetails,
  mapCenter: { lat: number; lng: number } | null | undefined,
  selectedSegmentId: string | null | undefined
): PendoDataCompletenessPayload {
  const status: DataCompletenessStatus = computeDataCompleteness(roadDetails);

  if (status === 'Full') {
    return { Data_Completeness: status };
  }

  if (status === 'Complete Miss') {
    return {
      Data_Completeness: status,
      Latitude: mapCenter?.lat ?? null,
      Longitude: mapCenter?.lng ?? null,
      Segment_ID: selectedSegmentId ?? null
    };
  }

  const roadMissing: boolean = roadDetails.roadName == null;
  const cityMissing: boolean = roadDetails.cityName == null;

  const payload: PendoDataCompletenessPayload = {
    Data_Completeness: status,
    Latitude: mapCenter?.lat ?? null,
    Longitude: mapCenter?.lng ?? null,
    Segment_ID: selectedSegmentId ?? null
  };

  if (roadMissing) {
    payload.Error = 'Road Name Not Found';
    payload.City_Name = roadDetails.cityName;
  } else if (cityMissing) {
    payload.Error = 'City Name Not Found';
    payload.Road_Name = roadDetails.roadName;
  }

  return payload;
}
